import { useParams, Link } from "react-router-dom";

const PAGES = {
  "about": {
    title: "About Us",
    content: `
      Digitoly QR is a next-generation QR code platform built for businesses, 
      marketers, and agencies who want more from their QR codes.

      We believe QR codes should do more than just redirect. They should capture leads, 
      filter reviews, deliver vCards, and provide deep analytics — all from one platform.

      Built in India. Used worldwide.
    `
  },
  "faq": {
    title: "Frequently Asked Questions",
    faqs: [
      { q: "What is a Dynamic QR code?", a: "A Dynamic QR code can have its destination URL changed at any time without reprinting the QR. Static QR codes are permanent once printed." },
      { q: "What happens when a customer scans a Lead QR?", a: "They see a form asking for their name, email, and phone number. After submitting, they are redirected to your destination URL and their details appear in your Leads dashboard." },
      { q: "How does the Feedback QR work?", a: "Customers rate their experience with 1-5 stars. If they give 4 or 5 stars, they are sent directly to your Google review page. Lower ratings show a feedback form so you can address issues privately." },
      { q: "Can I change the destination URL after printing?", a: "Yes, for Dynamic QR codes. Go to My QR Codes, click Edit on any QR, update the URL and save. The printed QR will start going to the new URL immediately." },
      { q: "What is the Free plan limit?", a: "Free users can create up to 5 QR codes. Leads, Feedback, Analytics and Bulk generation require a Pro or Agency plan." },
      { q: "Is my data secure?", a: "Yes. All data is stored in Supabase with Row Level Security — you can only see your own QR codes, leads and feedback. We never share your data." },
      { q: "How do I cancel my subscription?", a: "You can cancel anytime from your Razorpay subscription dashboard. Your access continues until the end of your billing period." },
    ]
  },
  "contact": {
    title: "Contact Us",
    content: "contact"
  },
  "privacy": null, // handled by LegalPages
  "terms": null,
};

export default function PublicPage() {
  const { page } = useParams();
  const data = PAGES[page];

  if (!data) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center text-white">
      <p>Page not found.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Nav */}
      <nav className="border-b border-white/5 px-8 py-4 flex justify-between items-center">
        <Link to="/" className="text-xl font-black italic uppercase tracking-tighter">Digitoly QR</Link>
        <Link to="/" className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-white transition-all">Login →</Link>
      </nav>

      <div className="max-w-3xl mx-auto px-8 py-20">
        <h1 className="text-4xl font-black uppercase italic tracking-tighter mb-12">{data.title}</h1>

        {/* FAQ Page */}
        {data.faqs && (
          <div className="space-y-4">
            {data.faqs.map((item, i) => (
              <details key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 group cursor-pointer">
                <summary className="text-sm font-black uppercase tracking-wide list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-gray-500 group-open:rotate-180 transition-transform">↓</span>
                </summary>
                <p className="text-gray-400 text-sm mt-4 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        )}

        {/* Contact Page */}
        {page === "contact" && (
          <div className="space-y-6">
            <p className="text-gray-400">Have questions or need support? Reach out to us.</p>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
              <p className="text-sm"><span className="text-gray-500 text-[10px] uppercase font-black block mb-1">Email</span>support@digitoly.com</p>
              <p className="text-sm"><span className="text-gray-500 text-[10px] uppercase font-black block mb-1">WhatsApp</span>+91 99205 41676</p>
              <p className="text-sm"><span className="text-gray-500 text-[10px] uppercase font-black block mb-1">Website</span>www.digitoly.com</p>
            </div>
          </div>
        )}

        {/* About Page */}
        {data.content && page !== "contact" && (
          <p className="text-gray-400 leading-relaxed whitespace-pre-line">{data.content}</p>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/5 mt-20 px-8 py-12 text-center">
      <div className="max-w-4xl mx-auto">
        <p className="text-2xl font-black italic uppercase tracking-tighter text-white mb-8">Digitoly QR</p>
        <div className="flex flex-wrap justify-center gap-8 mb-8">
          {[
            { label: "About Us", path: "/p/about" },
            { label: "FAQ", path: "/p/faq" },
            { label: "Contact", path: "/p/contact" },
            { label: "Privacy Policy", path: "/privacy" },
            { label: "Terms of Service", path: "/terms" },
          ].map(link => (
            <Link key={link.path} to={link.path} className="text-[10px] font-black uppercase tracking-widest text-gray-600 hover:text-white transition-all">
              {link.label}
            </Link>
          ))}
        </div>
        <p className="text-[10px] text-gray-700 uppercase tracking-widest">© {new Date().getFullYear()} Digitoly QR · All Rights Reserved · Made in India</p>
      </div>
    </footer>
  );
}